import { useState, useEffect, useRef } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

/* -------------------------------------------------------------------------- */
/*  FAQ data – kept outside the component so it isn’t recreated on every render */
/* -------------------------------------------------------------------------- */
const faqData: FAQItem[] = [
  {
    question: 'O que é a Carta do Eu Futuro?',
    answer:
      'A Carta do Eu Futuro é uma experiência profunda de autoconhecimento e planejamento emocional. Através de técnicas de PNL (Programação Neurolinguística) e visualização guiada, você escreve uma carta para a pessoa que deseja se tornar no futuro. É um compromisso consigo mesmo, uma conversa íntima com seu eu do futuro que será entregue no prazo escolhido por você.',
  },
  {
    question: 'Como funciona o processo de escrita da carta?',
    answer:
      'O processo é dividido em etapas guiadas. Primeiro, você passa por uma meditação de visualização para se conectar com sua versão futura. Em seguida, responde perguntas reflexivas sobre sua situação atual e seus desejos. Por fim, escreve sua carta com todo o sentimento e intenção. Todo o processo é intuitivo e emocionalmente envolvente.',
  },
  {
    question: 'Quais são os prazos de entrega disponíveis?',
    answer:
      'Oferecemos quatro opções de prazo para a entrega da sua carta: 1 ano, 2 anos, 3 anos ou 5 anos. Você escolhe o prazo que faz mais sentido para os objetivos e sonhos que deseja alcançar. Quando o prazo chegar, você receberá sua carta como uma mensagem do passado.',
  },
  {
    question: 'Quais formas de pagamento são aceitas?',
    answer:
      'Para o plano Premium, aceitamos pagamento via PIX, boleto bancário e cartão de crédito. O pagamento é processado de forma segura através da plataforma Asaas. Após a confirmação do pagamento, sua carta é salva e programada para entrega no prazo escolhido. O plano Gratuito não requer pagamento.',
  },
  {
    question: 'Minha carta é privada e segura?',
    answer:
      'Sim, absolutamente! Sua carta é 100% privada e confidencial. Apenas você tem acesso ao conteúdo. Utilizamos criptografia e armazenamento seguro para garantir que suas palavras permaneçam protegidas até o momento da entrega. Ninguém da nossa equipe tem acesso ao conteúdo da sua carta.',
  },
  {
    question: 'Posso editar minha carta depois de enviá‑la?',
    answer:
      'Após o envio, a carta é selada e não pode ser editada. Isso faz parte da experiência — a ideia é capturar exatamente o que você sente e pensa naquele momento. Quando receber a carta no futuro, ela será um retrato autêntico de quem você era.',
  },
  {
    question: 'Como vou receber minha carta no futuro?',
    answer:
      'Depende do seu plano. No plano Gratuito, quando o prazo escolhido chegar, você poderá acessar sua carta diretamente no seu painel pessoal (dashboard) no site. No plano Premium, além do acesso pelo painel, você receberá uma notificação por e‑mail e WhatsApp informando que sua carta está disponível, com a carta entregue diretamente na sua caixa de entrada com design exclusivo.',
  },
  {
    question: 'O que é a técnica de Ponte ao Futuro da PNL?',
    answer:
      'A Ponte ao Futuro é uma técnica poderosa da Programação Neurolinguística que utiliza a visualização criativa para projetar mentalmente um estado desejado no futuro. Ao imaginar vividamente quem você quer ser, seu cérebro começa a criar novos caminhos neurais que facilitam a realização desses objetivos. É ciência aplicada ao desenvolvimento pessoal.',
  },
  {
    question: 'Posso escrever mais de uma carta?',
    answer:
      'Sim! Você pode escrever quantas cartas quiser, cada uma com um prazo diferente ou para momentos diferentes da sua vida. Muitas pessoas escrevem cartas anuais como um ritual de autoconhecimento e acompanhamento da própria evolução.',
  },
  {
    question: 'E se eu mudar de e‑mail antes do prazo de entrega?',
    answer:
      'Não se preocupe! Você pode acessar suas cartas a qualquer momento através do seu painel no site usando seu login. Recomendamos manter seus dados de acesso atualizados para garantir que receberá a notificação quando sua carta estiver pronta para ser lida.',
  },
];

/* -------------------------------------------------------------------------- */
/*  Component: FAQSobreSection                                               */
/* -------------------------------------------------------------------------- */
const FAQSobreSection = () => {
  /* ---------------------------------------------------------------------- */
  /*  State handling                                                          */
  /* ---------------------------------------------------------------------- */
  // `null` means no item is opened; by default we open the first one (index 0)
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());

  /* ---------------------------------------------------------------------- */
  /*  Ref for IntersectionObserver                                            */
  /* ---------------------------------------------------------------------- */
  const sectionRef = useRef<HTMLElement>(null);

  /* ---------------------------------------------------------------------- */
  /*  IntersectionObserver – adds a simple fade‑in when the section enters   */
  /*  the viewport. It also cleans up properly if the observer can’t be      */
  /*  created (e.g., in a non‑browser environment).                           */
  /* ---------------------------------------------------------------------- */
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      // Server‑side rendering fallback – just show the section
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 },
    );

    const current = sectionRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
      observer.disconnect();
    };
  }, []);

  // Staggered reveal for accordion items
  useEffect(() => {
    if (!isVisible) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    faqData.forEach((_, i) => {
      timers.push(
        setTimeout(() => {
          setVisibleItems((prev) => new Set(prev).add(i));
        }, 600 + i * 100)
      );
    });
    return () => timers.forEach(clearTimeout);
  }, [isVisible]);

  /* ---------------------------------------------------------------------- */
  /*  Toggle logic – clicking an already‑opened item collapses it            */
  /* ---------------------------------------------------------------------- */
  const toggleItem = (index: number) => {
    setOpenIndex(prev => (prev === index ? null : index));
  };

  return (
    <section
      ref={sectionRef}
      className="bg-luxury-cream py-28 relative overflow-hidden"
    >
      {/* Decorative lines / background shapes */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent" />
      <div className="absolute bottom-0 right-0 w-72 h-72 bg-gold-500/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        {/* ==================== Header ==================== */}
        <div className="text-center mb-16">
          {/* Badge with expanding lines */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div
              className="h-px bg-gradient-to-r from-transparent to-gold-500/30 transition-all duration-1000 ease-out"
              style={{
                width: isVisible ? '4rem' : '0rem',
                opacity: isVisible ? 1 : 0,
              }}
            />
            <span
              className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-gold-600/60 transition-all duration-700 ease-out"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(-10px)',
                transitionDelay: '200ms',
              }}
            >
              Tire suas dúvidas
            </span>
            <div
              className="h-px bg-gradient-to-l from-transparent to-gold-500/30 transition-all duration-1000 ease-out"
              style={{
                width: isVisible ? '4rem' : '0rem',
                opacity: isVisible ? 1 : 0,
              }}
            />
          </div>

          {/* Title with blur reveal */}
          <h2
            className="font-['Playfair_Display'] text-4xl lg:text-5xl text-luxury-charcoal font-light mb-5 transition-all duration-800 ease-out"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.97)',
              filter: isVisible ? 'blur(0px)' : 'blur(6px)',
              transitionDelay: '300ms',
            }}
          >
            Perguntas <span className="italic text-gold-600">frequentes</span>
          </h2>

          {/* Subtitle fade up */}
          <p
            className="font-['Montserrat'] text-sm text-luxury-charcoal/40 max-w-xl mx-auto leading-relaxed font-light tracking-wide transition-all duration-700 ease-out"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(15px)',
              transitionDelay: '500ms',
            }}
          >
            Tudo o que você precisa saber sobre a experiência do Carta do Eu Futuro
          </p>
        </div>

        {/* ==================== Accordion ==================== */}
        <div className="max-w-3xl mx-auto space-y-3">
          {faqData.map((item, index) => {
            const isOpen = openIndex === index;
            const itemVisible = visibleItems.has(index);
            return (
              <div
                key={index}
                className={`rounded-xl border transition-all duration-500 overflow-hidden ${
                  isOpen
                    ? 'border-gold-500/25 bg-white shadow-[0_8px_30px_rgba(201,168,76,0.06)]'
                    : 'border-gold-500/10 bg-white/60 hover:border-gold-500/20 hover:bg-white/80'
                }`}
                style={{
                  opacity: itemVisible ? 1 : 0,
                  transform: itemVisible
                    ? 'translateY(0) translateX(0)'
                    : `translateY(20px) translateX(${index % 2 === 0 ? '-15px' : '15px'})`,
                  transition: `opacity 0.6s ease-out, transform 0.6s ease-out, border-color 0.5s, background-color 0.5s, box-shadow 0.5s`,
                }}
              >
                {/* ----- Header (clickable) ----- */}
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full flex items-center gap-5 px-7 py-5 text-left cursor-pointer group"
                >
                  {/* Number badge */}
                  <span
                    className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center font-['Montserrat'] text-xs font-semibold transition-all duration-500 ${
                      isOpen
                        ? 'bg-gradient-to-br from-gold-500 to-gold-600 text-white'
                        : 'bg-gold-500/10 text-gold-600 group-hover:bg-gold-500/20'
                    }`}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>

                  {/* Question text */}
                  <span
                    className={`flex-1 font-['Montserrat'] text-sm font-medium transition-colors duration-300 ${
                      isOpen ? 'text-luxury-charcoal' : 'text-luxury-charcoal/70'
                    }`}
                  >
                    {item.question}
                  </span>

                  {/* Arrow icon */}
                  <span
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 ${
                      isOpen
                        ? 'bg-gold-500/15 rotate-180'
                        : 'bg-gold-500/5 group-hover:bg-gold-500/10'
                    }`}
                  >
                    <i
                      className={`ri-arrow-down-s-line text-lg transition-colors duration-300 ${
                        isOpen ? 'text-gold-600' : 'text-luxury-charcoal/30'
                      }`}
                    />
                  </span>
                </button>

                {/* ----- Collapsible answer ----- */}
                <div
                  className={`transition-all duration-500 ease-in-out ${
                    isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-7 pb-6 pl-[4.75rem]">
                    <div className="relative pl-5">
                      <div className="absolute left-0 top-1 bottom-1 w-0.5 bg-gradient-to-b from-gold-500/40 to-gold-500/10 rounded-full" />
                      <p className="font-['Montserrat'] text-sm text-luxury-charcoal/50 leading-relaxed font-light tracking-wide">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ==================== Bottom CTA ==================== */}
        <div
          className="text-center mt-14 transition-all duration-700 ease-out"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transitionDelay: '1600ms',
          }}
        >
          <p className="font-['Montserrat'] text-xs text-luxury-charcoal/35 tracking-wide mb-4">
            Ainda tem dúvidas? Estamos aqui para ajudar.
          </p>
          <a
            href="/faq"
            className="inline-flex items-center gap-2 font-['Montserrat'] text-xs font-semibold text-gold-600 hover:text-gold-700 transition-colors duration-300 tracking-wider uppercase cursor-pointer group"
          >
            Ver todas as perguntas
            <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform duration-300" />
          </a>
          {/* Animated golden line */}
          <div
            className="mx-auto mt-6 h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent transition-all duration-1200 ease-out"
            style={{
              width: isVisible ? '12rem' : '0rem',
              opacity: isVisible ? 1 : 0,
              transitionDelay: '1800ms',
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default FAQSobreSection;
